import { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Recovery.module.css';

import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  COMPLETE,
  ENTER,
  ENTER_VERIFICATION_CODE,
  PREV,
  RECOVER_ACCOUNT,
  RE_REQUEST,
  SENT_VERIFICATION_CODE,
  CONFIRM,
  VERIFICATION_CODE,
  VERIFICATION_HELP,
  VERIFICATION_MSG,
} from '../../../constants';
import { maskEmail } from '../../../utils/util';
import Complete from '../Complete';

import {
  useGetVerificationCodeForRecoveryMutation,
  useReactivateAccountMutation,
  useVerifyCodeForRecoveryMutation,
} from '../../../services/api';

const recoverySchema = yup.object({
  certPwd: yup.string().required(),
});

export default function Recovery() {
  const cx = classNames.bind(styles);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { email } = state.info;

  const [getVerificationCode] = useGetVerificationCodeForRecoveryMutation();
  const [verifyCode] = useVerifyCodeForRecoveryMutation();
  const [reactivateAccount] = useReactivateAccountMutation();

  const {
    register,
    watch,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: yupResolver(recoverySchema),
    defaultValues: { certPwd: '' },
  });

  const maskedEmail = maskEmail(email as string);

  const [isVerified, setIsVerified] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [verifiedMsg, setVerifiedMsg] = useState('');

  const verify = async () => {
    try {
      const data: any = await verifyCode({
        email,
        certPwd: getValues('certPwd'),
      }).unwrap();

      setVerifiedMsg(data.message);
      setIsVerified(data.message === 'OK');
    } catch (error) {
      console.error('Failed to verify', error);
    }
  };

  const complete = async () => {
    try {
      await reactivateAccount(email);
    } catch (error) {
      console.error('Fail to recover', error);
    }
    setIsComplete(true);
  };

  const getCode = async () => {
    try {
      const data: any = await getVerificationCode(email);
    } catch (error) {
      console.error('Fail to get verification code', error);
    }
  };

  useEffect(() => {
    getCode();
  }, []);

  return (
    <>
      <h2>
        {isComplete ? `${RECOVER_ACCOUNT} ${COMPLETE}!` : RECOVER_ACCOUNT}
      </h2>
      <div>
        {isComplete ? (
          <Complete name="recovery" />
        ) : (
          <>
            <div className={cx('info_account')}>
              <p className={cx('desc')}>{SENT_VERIFICATION_CODE}</p>
              <div className={cx('send_email_area')}>
                <div className={cx('flex', 'center')}>
                  <div>
                    <span>{maskedEmail}</span>
                  </div>
                  <button
                    className={cx('btn', 'b_t', 'dull')}
                    onClick={getCode}
                  >
                    <span>{RE_REQUEST}</span>
                  </button>
                </div>
              </div>
              <div className={cx('input_area')}>
                <div className={cx('input_box')}>
                  <span
                    className={cx('blind')}
                  >{`${VERIFICATION_CODE} ${ENTER}`}</span>
                  <input
                    type="text"
                    title={`${VERIFICATION_CODE} ${ENTER}`}
                    placeholder={ENTER_VERIFICATION_CODE}
                    autoCapitalize="none"
                    {...register('certPwd')}
                  />

                  {!isVerified && (
                    <span
                      className={cx('msg', { invalid: verifiedMsg !== 'OK' })}
                    >
                      {verifiedMsg}
                    </span>
                  )}
                </div>
                <div className={cx('btn_wrap')}>
                  {!isVerified && (
                    <button
                      type="button"
                      className={cx('btn', 'b_t', 'dull', {
                        'is--disabled': !watch('certPwd'),
                      })}
                      onClick={verify}
                    >
                      <span>{`${VERIFICATION_CODE} ${CONFIRM}`}</span>
                    </button>
                  )}
                  {isVerified && (
                    <p className={cx('done_t')}>{VERIFICATION_MSG}</p>
                  )}
                </div>
                {!isVerified && (
                  <p className={cx('desc', 'dull')}>
                    <span
                      dangerouslySetInnerHTML={{ __html: VERIFICATION_HELP }}
                    ></span>
                  </p>
                )}
              </div>
            </div>

            <div className={cx('btn_wrap', 'bottom', 'cell')}>
              <button
                type="button"
                className={cx('btn', 'b_t', 'dull', 's44')}
                onClick={() => navigate(-1)}
              >
                <span>{PREV}</span>
              </button>
              <button
                type="button"
                className={cx('btn', 'b_t', 's44', {
                  'is--disabled': !isVerified,
                })}
                onClick={complete}
              >
                <span>{COMPLETE}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
